/** S3 · Never looks like an ad + reel rail (#never) — THE FEED homepage, design.md Part III.
 *
 * The shared runtime pops the headline's words and parks the karaoke pill on the
 * first red key word as a 4px bar. Here: (1) each red word gets `data-ink` so its
 * CSS `::after` can print the one-shot ink ghost of the stamp; (2) once the pill
 * has parked, the bar is stretched under the whole red pair ("CONTENT NEVER" /
 * "ما يبين") when both words sit on one line; (3) the rail's start padding is
 * measured so the first card lines up with the .wrap column at every width
 * (the track carries the 4K zoom itself). Videos and the scrub line are handled
 * by the runtime (data-reel) and scrub.ts (data-scrub-for); (4) the reels loop
 * while in view, so the rail head carries a pause toggle (WCAG 2.2.2) that
 * holds every rail video paused until it is pressed again. */
import { canAutoplay, onReveal, pauseReel, playReel } from "./runtime";

const BASE_W = 100; // the runtime's pill base width (px) that scaleX multiplies

/** The 4K step (--ui-zoom) applied to `el` — rects are in screen px, while
 * transforms/paddings inside a zoomed box are in its own (unzoomed) px. */
function zoomOf(el: Element): number {
  const z = parseFloat(getComputedStyle(el).getPropertyValue("--ui-zoom"));
  return Number.isFinite(z) && z > 0 ? z : 1;
}

function stretchBar(head: HTMLElement): void {
  const pill = head.querySelector<HTMLElement>(".cap-pill");
  const reds = Array.from(head.querySelectorAll<HTMLElement>(".w.is-red"));
  if (!pill || reds.length < 2) return;
  const rects = reds.map((w) => w.getBoundingClientRect());
  const first = rects[0];
  if (!first) return;
  // split across lines (narrow phones): one bar cannot follow the pair, so the
  // pill steps aside and each red word carries its own underline instead
  const split = rects.some(
    (r) => Math.abs(r.top - first.top) > first.height * 0.4,
  );
  head.classList.toggle("is-split", split);
  for (const word of reds) word.classList.toggle("is-ul", split);
  if (split) return;
  const z = zoomOf(head);
  const base = head.getBoundingClientRect();
  const left = Math.min(...rects.map((r) => r.left));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  // the pill is anchored at the heading's inline-start: measure from that edge
  const rtl = getComputedStyle(head).direction === "rtl";
  const origin = rtl ? base.right - BASE_W * z : base.left;
  const pad = first.height * 0.04;
  const x = (left - origin - pad) / z;
  const y = (bottom - base.top) / z - 2;
  const w = (right - left + pad * 2) / z;
  pill.style.transform = `translate(${x}px, ${y}px) scaleX(${w / BASE_W})`;
  for (const word of reds) word.classList.add("is-key-on");
}

/** Keep the bar right after any reflow (rotation, resize, late font): the
 * runtime's own observer re-parks the pill on word 1 alone, and this one —
 * created later, so it runs after it — stretches it back over the pair. */
function keepBar(head: HTMLElement): void {
  stretchBar(head);
  if (!("ResizeObserver" in window)) return;
  let raf = 0;
  new ResizeObserver(() => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => stretchBar(head));
  }).observe(head);
}

function alignRail(): void {
  const track = document.getElementById("never-rail");
  const head = document.querySelector<HTMLElement>("#never .nrail__head");
  if (!track || !head) return;
  const measure = (): void => {
    const t = track.getBoundingClientRect();
    const h = head.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === "rtl";
    const offset = rtl ? t.right - h.right : h.left - t.left;
    if (offset <= 0) return;
    const z = zoomOf(track);
    track.style.setProperty("--rail-pad", `${(offset / z).toFixed(2)}px`);
  };
  if ("ResizeObserver" in window) new ResizeObserver(measure).observe(track);
  else window.addEventListener("resize", measure, { passive: true });
  measure();
}

/** The rail's pause toggle. The runtime's seen-observer still calls playReel()
 * as cards scroll into view; while paused, every `play` is answered with
 * pauseReel() (which also frees the decode-budget slot). On resume, the cards
 * that are in view play again. */
function initPause(): void {
  const rail = document.querySelector<HTMLElement>("#never .nrail");
  const btn = rail?.querySelector<HTMLButtonElement>(".nrail__pause");
  if (!rail || !btn || !canAutoplay()) return;
  const videos = Array.from(
    rail.querySelectorAll<HTMLVideoElement>("video[data-reel]"),
  );
  if (videos.length === 0) return;
  btn.hidden = false;
  const inView = new Set<HTMLVideoElement>();
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const v = e.target as HTMLVideoElement;
          if (e.isIntersecting) inView.add(v);
          else inView.delete(v);
        }
      },
      { threshold: 0.35 },
    );
    for (const v of videos) io.observe(v);
  }
  let paused = false;
  for (const v of videos) {
    v.addEventListener("play", () => {
      if (paused) pauseReel(v);
    });
  }
  btn.addEventListener("click", () => {
    paused = !paused;
    rail.classList.toggle("is-paused", paused);
    if (paused) for (const v of videos) pauseReel(v);
    else for (const v of inView) playReel(v);
  });
}

export function init(): void {
  alignRail();
  initPause();
  const head = document.querySelector<HTMLElement>("#never .never__h2");
  if (!head) return;
  for (const w of head.querySelectorAll<HTMLElement>(".w.is-red")) {
    w.setAttribute("data-ink", w.textContent ?? "");
  }
  onReveal((el) => {
    if (el !== head) return;
    if (!document.documentElement.classList.contains("js-motion")) return;
    const words = head.querySelectorAll(".w").length;
    const step = parseFloat(head.style.getPropertyValue("--cap-stagger")) || 55;
    // the runtime walks the pill word by word (≥70ms a step) to the first key
    // word; stretch once it has parked and the red pair has stamped down
    const parked = 80 + words * Math.max(step, 70) + 900;
    window.setTimeout(() => keepBar(head), parked);
  });
}
